/**
 * Deterministic dealing: build the set, shuffle with a seed, and partition it
 * into hands, boneyard, and set-aside according to the frozen RulesSnapshot.
 *
 * Pure functions only: no I/O, no clock, no framework dependencies. The same
 * snapshot and seed always produce the same partition.
 */

import { SEAT_COUNT, type RulesSnapshot } from "@domino/types";

import { shuffle } from "./rng";
import { TILE_SET_SIZE, buildTileSet } from "./tiles";

/** The dealt partition of the 28-tile set. */
export interface DealResult {
  /** Tile ids held per seat, seat 0 first, in seat order. */
  hands: number[][];
  /** Tile ids left in the boneyard (draw modes). */
  boneyard: number[];
  /** Tile ids set aside unseen (no-draw modes). */
  setAside: number[];
}

/**
 * Validate a rules snapshot for dealing; throws a plain Error with a clear
 * message when the snapshot is inconsistent.
 */
function validateSnapshot(rules: RulesSnapshot): void {
  const expectedSeatCount = SEAT_COUNT[rules.mode];
  if (rules.seatCount !== expectedSeatCount) {
    throw new Error(
      `Inconsistent rules snapshot: seatCount ${String(rules.seatCount)} does not match mode "${rules.mode}" (expected ${String(expectedSeatCount)}).`,
    );
  }
  if (!Number.isInteger(rules.handSize) || rules.handSize <= 0) {
    throw new Error(
      `Inconsistent rules snapshot: handSize must be a positive integer, got ${String(rules.handSize)}.`,
    );
  }
  const totalDealt = rules.seatCount * rules.handSize;
  if (totalDealt > TILE_SET_SIZE) {
    throw new Error(
      `Inconsistent rules snapshot: ${String(rules.seatCount)} seats x handSize ${String(rules.handSize)} deals ${String(totalDealt)} tiles, exceeding the ${String(TILE_SET_SIZE)}-tile set.`,
    );
  }
}

/**
 * Defensive post-deal invariant check: hands, boneyard, and set-aside must
 * partition the 28-tile set into distinct ids and hand sizes must match the
 * rules. Throws a plain Error otherwise (guards future refactors; the
 * construction already guarantees these invariants).
 */
function validatePartition(
  hands: readonly number[][],
  boneyard: readonly number[],
  setAside: readonly number[],
  rules: RulesSnapshot,
): void {
  if (hands.length !== rules.seatCount) {
    throw new Error(
      `Internal deal error: dealt ${String(hands.length)} hands, expected ${String(rules.seatCount)}.`,
    );
  }
  const all = [...hands.flat(), ...boneyard, ...setAside];
  if (all.length !== TILE_SET_SIZE || new Set(all).size !== TILE_SET_SIZE) {
    throw new Error(
      `Internal deal error: hands, boneyard, and set-aside must partition the ${String(TILE_SET_SIZE)}-tile set into distinct ids.`,
    );
  }
  for (const hand of hands) {
    if (hand.length !== rules.handSize) {
      throw new Error(
        `Internal deal error: hand size ${String(hand.length)} does not match rules handSize ${String(rules.handSize)}.`,
      );
    }
  }
}

/**
 * Deal tiles for a round.
 *
 * Builds the double-six set, shuffles it with `seed` (Fisher-Yates, see
 * rng.ts), then deals `rules.handSize` tiles to each of `rules.seatCount`
 * seats in seat order starting from seat 0. Leftover tiles go to the boneyard
 * when `rules.boneyard` is "draw" (1v1 leaves 14, 3P default leaves 7), or to
 * set-aside when it is "none" (2v2 deals all 28; the 3P-alternative deal with
 * handSize 9 deals 27 and sets aside exactly one tile). The boneyard floor is
 * a draw-time rule and does not affect the initial deal.
 *
 * Throws a plain Error when the snapshot is inconsistent: seatCount does not
 * match the mode, handSize is not a positive integer, or seatCount x handSize
 * exceeds the 28-tile set.
 */
export function dealTiles(rules: RulesSnapshot, seed: number): DealResult {
  validateSnapshot(rules);

  const shuffled = shuffle(
    buildTileSet().map((tile) => tile.id),
    seed,
  );

  const hands: number[][] = [];
  let cursor = 0;
  for (let seat = 0; seat < rules.seatCount; seat++) {
    hands.push(shuffled.slice(cursor, cursor + rules.handSize));
    cursor += rules.handSize;
  }
  const leftover = shuffled.slice(cursor);

  const boneyard = rules.boneyard === "draw" ? leftover : [];
  const setAside = rules.boneyard === "draw" ? [] : leftover;

  validatePartition(hands, boneyard, setAside, rules);
  return { hands, boneyard, setAside };
}
